import * as React from 'react';

export enum USAGE_CODES {
    USED = 'Used',
    NOT_USED = 'Not used',
    NOT_RECEIVED = 'Not received',
    DOES_NOT_WORK = 'Does not work',
    DAMAGED_IN_TRANSIT = 'Damaged in transit',
    WRONG_PART = 'Wrong part',
}

export const NOTE_PLACEHOLDERS = {
  [USAGE_CODES.USED]: 'What was wrong with the original part?',
  [USAGE_CODES.NOT_USED]: 'Why was the new part not used?',
  [USAGE_CODES.NOT_RECEIVED]: 'Were there any delivery issues on site?',
  [USAGE_CODES.DOES_NOT_WORK]: 'What is wrong with the new part?',
  [USAGE_CODES.DAMAGED_IN_TRANSIT]: 'What is wrong with the new part?',
  [USAGE_CODES.WRONG_PART]: 'Is a different part needed?',
};

type PartKeys = 'id' | 'externalId'| 'externalNumber' | 'description' | 'returnable' | 'trackingNumber' | 'carrier' | 'carrierOther' | 'returnInstruction';
type UsageKeys = 'usageCode' | 'customerRetained' | 'returnTracking' | 'returnCarrier' | 'returnCarrierOtherName' | 'note';

export interface AdditionalPartSectionValues {
  serial_number?: string;
  internal_number?: string;
  rma_number?: string;
  return_label_requested?: boolean;
}

export interface AdditionalPartValues {
  new?: AdditionalPartSectionValues;
  defective?: AdditionalPartSectionValues;
}

export type FieldName = 'serial_number' | 'internal_number' | 'rma_number';
export type Section = 'new' | 'defective';

export interface AdditionalPartField {
  field_name: FieldName;
  field_label: string;
  required: boolean;
}

export type Part = {
    [k in PartKeys]: k extends 'id' ? number : string;
}

export type PartUsage = {
    [k in UsageKeys]: string;
} & {
    additionalPartValues?: AdditionalPartValues;
    additionalPartFields?: AdditionalPartField[];
}

export interface PartEntry {
  id: number;
  part: Part;
  usage: PartUsage;
}

export interface PartsProviderInputsValue {
  rmaNumber: boolean;
  serialNumber: boolean;
  internalNumber: boolean;
  internalNumberAlias: string;
}

export interface PartsFormV3Props {
  parts: PartEntry[];
  onChange: (parts: PartEntry[]) => void;
}

export interface PartsProviderInputsProps {
  value: PartsProviderInputsValue;
  onChange: (value: PartsProviderInputsValue) => void;
  disabled?: boolean;
}

export interface PartsProviderSettingsDeliveryRequirement {
  value: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export interface PartsProviderSettingsProps {
  fields: AdditionalPartField[];
  onChange: (fields: AdditionalPartField[]) => void;
  showProviderInputs?: boolean;
  deliveryRequirement?: PartsProviderSettingsDeliveryRequirement;
  disabled?: boolean;
}

export interface PartsFormV3ModalProps {
  partData: Part;
  usageData: PartUsage;
  openEditModal: boolean;
  setOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  partsCorrelationId: string;
  workOrderId: number;
  workOrderProjectName: string;
  workOrderStatus: string;
  workOrderUserRole: string;
  usageCodes?: string[];
  isProvider: boolean;
  addPart: boolean;
  companyId?: number;
  /** Config array defining which additional part fields to render and whether they are required. */
  additionalPartFields?: AdditionalPartField[];
}

export class PartsFormV3 extends React.Component<PartsFormV3Props, any> {}

export class PartsFormV3Modal extends React.Component<PartsFormV3ModalProps, any> {}

export class PartsProviderInputs extends React.Component<PartsProviderInputsProps, any> {}

export class PartsProviderSettings extends React.Component<PartsProviderSettingsProps, any> {}

/** A part file record as returned by the parts-and-shipments file APIs. `url` is a
 * short-lived presigned download URL, present only on the GET files response. */
export interface PartFile {
  id: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  created_at: string;
  url?: string;
}

export interface PartV3 {
  id?: number;
  external_id: string;
  description: string;
  external_number?: string;
  usage_code?: {
    id?: number;
    name: string;
  };
  note?: string;
  incoming_tracking?: {
    tracking_id: string;
    carrier_name: string;
  };
  return_tracking?: {
    tracking_id: string;
    carrier_name: string;
    not_returned_reason?: string | null;
  };
  return_policy: {
    is_returnable: boolean;
    return_instruction?: string;
  };
  additional_part_values?: AdditionalPartValues;
  files?: PartFile[];
}
